import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, IconButton, Page, PageHeader, Pagination, SearchInput, Segmented, TableSkeleton } from "../../../components/ui";
import { getVideoPerformance } from "../../../services/videoPerformance";
import Chart from "react-apexcharts";
import { formatMoney } from "../payments-ledger/shared";
import { isoDay } from "../main/dashboard/PeriodFilter";
import VideoPerformanceDetail, { audienceChart } from "./VideoPerformanceDetail";
import "../payments-ledger/payments.css";
import "./video-performance.css";

const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const percent = (value) => (value === null || value === undefined ? "—" : `${value} %`);
const DAY = 24 * 60 * 60 * 1000;

/** Watch time: « 45 s », « 12 min », « 2 h 05 », « 1 234 h ». */
export const formatDuration = (seconds) => {
  const s = Math.round(seconds || 0);
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const hours = Math.floor(s / 3600);
  if (hours >= 100) return `${fmt(hours)} h`;
  return `${hours} h ${String(Math.round((s % 3600) / 60)).padStart(2, "0")}`;
};

const COLUMNS = [
  { sort: "views", label: "Views" },
  { sort: "watchTime", label: "Watch time", title: "Estimated from the playback position of each play started in the app or on the site" },
  { sort: "completion", label: "Watched to the end", title: "Share of the plays started in the app or on the site that reached 90 % (5 plays at least)" },
  { sort: "watched", label: "Average share watched" },
  { sort: "comments", label: "Comments" },
  { sort: "likes", label: "Likes" },
  { sort: "shares", label: "Shares", shorts: true },
  { sort: "thanks", label: "Merci" },
  { sort: "watchlist", label: "Watch later" },
  { sort: "sales", label: "Sales" },
];

const METRIC_LABELS = {
  views: "Views",
  totalViews: "Total views",
  trend: "Views gained",
  watchTime: "Watch time",
  completion: "Watched to the end",
  watched: "Average share watched",
  comments: "Comments",
  likes: "Likes",
  shares: "Shares",
  thanks: "Merci",
  watchlist: "Watch later",
  sales: "Sales",
  recent: "Views",
};
const METRIC_VALUE = {
  views: (i) => i.views,
  totalViews: (i) => i.totalViews,
  trend: (i) => (i.previousViews === null || i.previousViews === undefined ? 0 : i.views - i.previousViews),
  watchTime: (i) => Math.round((i.watchSeconds || 0) / 3600),
  completion: (i) => (i.started >= 5 ? i.completion : 0),
  watched: (i) => (i.started >= 5 ? i.watched : 0),
  comments: (i) => i.comments,
  likes: (i) => i.likes,
  shares: (i) => i.shares || 0,
  thanks: (i) => i.thanksUsd,
  watchlist: (i) => i.watchlist,
  sales: (i) => i.sales,
  recent: (i) => i.views,
};

/** Horizontal bars of the 10 first videos for the ranking metric. */
export const topChart = (items, sort, t) => {
  const value = METRIC_VALUE[sort] || METRIC_VALUE.views;
  const percent = sort === "completion" || sort === "watched";
  const money = sort === "thanks";
  const hours = sort === "watchTime";
  const short = (title) => (title.length > 38 ? `${title.slice(0, 36)}…` : title);
  const format = (v) => (percent ? `${v} %` : money ? formatMoney(v, "USD") : hours ? `${fmt(v)} h` : fmt(v));
  return {
    count: items.length,
    series: [{ name: t(METRIC_LABELS[sort] || "Views"), data: items.map(value) }],
    options: {
      chart: { id: "perf-top", background: "transparent", toolbar: { show: false }, animations: { enabled: false }, fontFamily: "inherit" },
      plotOptions: { bar: { horizontal: true, barHeight: "65%", borderRadius: 3 } },
      colors: ["#1176BC"],
      dataLabels: { enabled: true, formatter: format, style: { colors: ["#fff"] } },
      grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
      xaxis: { categories: items.map((i) => short(i.title)), labels: { style: { colors: "#9b9ba6" }, formatter: (v) => (percent ? `${v} %` : fmt(v)) } },
      yaxis: { labels: { style: { colors: "#d4d4da" }, maxWidth: 220 } },
      tooltip: { theme: "dark", x: { formatter: (v, { dataPointIndex }) => items[dataPointIndex]?.title || v }, y: { formatter: format } },
    },
  };
};

/** CSV of the current page. */
const exportCsv = (data) => {
  const head = ["Rang", "Vidéo", "Vues (période)", "Vues (période précédente)", "Évolution %", "Vues (total)", "Temps regardé (h)", "Lectures commencées", "Lectures terminées", "Regardée jusqu'au bout %", "Part regardée %", "Taux de fin JW %", "Commentaires", "J'aime", "Partages", "Merci", "Merci (USD)", "À regarder plus tard", "Ventes", "Revenu"];
  const rows = data.items.map((i) => [
    i.rank, i.title, i.views, i.previousViews ?? "", i.trend ?? "", i.totalViews, ((i.watchSeconds || 0) / 3600).toFixed(1), i.started, i.completed, i.completion ?? "", i.watched ?? "", i.jwCompleteRate ?? "", i.comments, i.likes, i.shares || 0, i.thanks, i.thanksUsd, i.watchlist, i.sales,
    i.revenue.map((r) => `${r.total} ${r.currency}`).join(" + "),
  ]);
  const csv = [head, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `performances-videos-${data.period}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

/** Change of the views of one video against the previous period. */
const Trend = ({ item }) => {
  const { t } = useTranslation();
  if (item.previousViews === null || item.previousViews === undefined) return null;
  if (!item.previousViews) return item.views ? <small className="perf-delta" data-tone="up">{t("new")}</small> : null;
  const tone = item.trend > 0 ? "up" : item.trend < 0 ? "down" : "flat";
  return (
    <small className="perf-delta" data-tone={tone} title={t("{{count}} views over the previous period", { count: item.previousViews, formatted: fmt(item.previousViews) })}>
      <i className={tone === "up" ? "ri-arrow-up-line" : tone === "down" ? "ri-arrow-down-line" : "ri-subtract-line"} aria-hidden="true"></i>
      {Math.abs(item.trend)} %
    </small>
  );
};

/** What stands out over the period: rising, best kept, to rework, most engaging. */
const HIGHLIGHTS = {
  rising: { icon: "ri-rocket-line", label: "Biggest rise", text: (h, t) => t("+{{count}} views", { count: h.value, formatted: fmt(h.value) }) + (h.trend !== null && h.trend !== undefined ? ` (+${h.trend} %)` : "") },
  retention: { icon: "ri-award-line", label: "Watched to the end the most", text: (h, t) => t("{{value}} % of {{count}} plays", { value: h.value, count: h.started, formatted: fmt(h.started) }) },
  dropoff: { icon: "ri-scissors-cut-line", label: "Much viewed, often left early", text: (h, t) => t("only {{value}} % watched to the end", { value: h.value }) },
  engagement: { icon: "ri-chat-heart-line", label: "Most engaging", text: (h, t) => t("{{value}} interactions per 100 views", { value: h.value }) },
};

const Highlights = ({ items, onOpen }) => {
  const { t } = useTranslation();
  if (!items?.length) return null;
  return (
    <div className="perf-highlights">
      {items.map((h) => {
        const def = HIGHLIGHTS[h.kind];
        if (!def) return null;
        return (
          <button type="button" key={h.kind} className="ui-card perf-highlight" onClick={() => onOpen(h.id)}>
            <span className="perf-highlight-label">
              <i className={def.icon} aria-hidden="true"></i> {t(def.label)}
            </span>
            <strong title={h.title}>{h.title}</strong>
            <span className="ui-hint">{def.text(h, t)}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Every video over a period: views and their change, watch time, plays watched to the end,
 * share watched, comments, likes, shares, Merci, watch later, exclusive sales; audience per day,
 * top 10 and what stands out. Sorted by any column.
 */
const VideoPerformance = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("30d");
  const [range, setRange] = useState(() => ({ start: isoDay(Date.now() - 29 * DAY), end: isoDay(Date.now()) }));
  const [format, setFormat] = useState("videos");
  const [sort, setSort] = useState("views");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [opened, setOpened] = useState(null);
  const rangeValid = Boolean(range.start && range.end && range.start <= range.end);
  const periodParams = period === "custom" ? { period, start: range.start, end: range.end } : { period };

  const load = useCallback(
    (fresh) => {
      if (period === "custom" && !rangeValid) return;
      setData(null);
      setError(false);
      getVideoPerformance({ ...(period === "custom" ? { period, start: range.start, end: range.end } : { period }), format, sort, page, q: search.trim(), ...(fresh === true ? { fresh: 1 } : {}) })
        .then(setData)
        .catch(() => setError(true));
    },
    [period, range, rangeValid, format, sort, page, search]
  );
  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const reset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };
  const totals = data?.totals;
  const prev = data?.previous;
  // Variation par rapport à la période précédente de même durée
  const delta = (now, before) => {
    if (!prev || before === undefined || before === null) return null;
    if (!before) return now ? { text: t("new"), tone: "up" } : null;
    const change = Math.round(((now - before) / before) * 100);
    return { text: `${change > 0 ? "+" : ""}${change} %`, tone: change > 0 ? "up" : change < 0 ? "down" : "flat" };
  };
  const card = (value, label, change, hint) => (
    <div className="ui-card ledger-stat" title={hint}>
      <strong>{totals ? value : "—"}</strong>
      <span>{label}</span>
      {change && (
        <small className="perf-delta" data-tone={change.tone}>
          {change.text} {t("vs previous period")}
        </small>
      )}
    </div>
  );
  const completion = (x) => (x?.started ? Math.round((x.completed / x.started) * 100) : null);
  const top = data?.items?.length ? topChart(data.items.slice(0, 10), sort, t) : null;
  const audience = data?.daily?.some((d) => d.views || d.started) ? audienceChart(data.daily, t, "perf-daily") : null;
  const columns = COLUMNS.filter((c) => !c.shorts || format !== "videos");
  // Navigation d'une fiche à l'autre, dans l'ordre du tableau
  const order = data?.items?.map((i) => i.id) || [];
  const at = order.indexOf(opened);

  return (
    <Page>
      <PageHeader
        title={t("Video performance")}
        subtitle={t("Views, how far people watch, comments, likes and sales of every video.")}
        actions={
          <>
            {data?.items?.length > 0 && <IconButton icon="ri-download-2-line" label={t("Export (CSV)")} onClick={() => exportCsv(data)} />}
            <IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={() => load(true)} />
          </>
        }
      />

      <div className="perf-filters">
        <Segmented
          label={t("Period")}
          value={period}
          onChange={reset(setPeriod)}
          options={[
            { value: "7d", label: t("7 days") },
            { value: "30d", label: t("30 days") },
            { value: "90d", label: t("90 days") },
            { value: "365d", label: t("1 year") },
            { value: "all", label: t("All time") },
            { value: "custom", label: t("Dates") },
          ]}
        />
        {period === "custom" && (
          <div className="perf-range">
            <label>
              <span>{t("From")}</span>
              <input type="date" className="ui-input" value={range.start} max={range.end || isoDay(Date.now())} onChange={(e) => reset(setRange)({ ...range, start: e.target.value })} />
            </label>
            <label>
              <span>{t("To")}</span>
              <input type="date" className="ui-input" value={range.end} min={range.start} max={isoDay(Date.now())} onChange={(e) => reset(setRange)({ ...range, end: e.target.value })} />
            </label>
            {!rangeValid && <span className="perf-range-error" role="alert">{t("The start date must be before the end date")}</span>}
          </div>
        )}
        <Segmented
          label={t("Format")}
          value={format}
          onChange={reset(setFormat)}
          options={[
            { value: "videos", label: t("Videos") },
            { value: "shorts", label: t("Shorts") },
            { value: "all", label: t("All") },
          ]}
        />
        {data?.computedAt && (
          <span className="ui-hint perf-computed">{t("Figures of {{time}}", { time: new Date(data.computedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}</span>
        )}
      </div>

      <div className="ledger-stats perf-stats">
        {card(fmt(totals?.views), t("Views"), delta(totals?.views, prev?.views))}
        {card(formatDuration(totals?.watchSeconds), t("Watch time"), delta(totals?.watchSeconds, prev?.watchSeconds), t("Estimated from the playback position of each play started in the app or on the site"))}
        {card(completion(totals) !== null ? `${completion(totals)} %` : "—", t("Plays watched to the end"), prev && completion(prev) !== null && completion(totals) !== null ? { text: `${completion(totals) - completion(prev) > 0 ? "+" : ""}${completion(totals) - completion(prev)} pts`, tone: completion(totals) >= completion(prev) ? "up" : "down" } : null)}
        {card(fmt(totals?.comments), t("Comments"), delta(totals?.comments, prev?.comments))}
        {card(fmt(totals?.likes), t("Likes"), delta(totals?.likes, prev?.likes))}
        {format === "videos"
          ? card(fmt(totals?.watchlist), t("Watch later"), null)
          : card(fmt(totals?.shares), t("Shares"), delta(totals?.shares, prev?.shares), t("Shares of shorts reported by the app"))}
        {card(formatMoney(totals?.thanksUsd || 0, "USD"), t("Merci received"), delta(totals?.thanksUsd, prev?.thanksUsd))}
        {card(fmt(totals?.sales), t("Exclusive sales"), delta(totals?.sales, prev?.sales))}
      </div>

      <Highlights items={data?.highlights} onOpen={setOpened} />

      {(audience || top) && (
        <div className="perf-charts">
          {audience && (
            <section className="ui-card perf-top">
              <h2>{t("Audience per day")}</h2>
              <Chart type="line" height={Math.max(260, top ? 34 * top.count : 0)} options={audience.options} series={audience.series} />
              {data.period === "all" && <p className="ui-hint">{t("« All time »: the charts cover the last 90 days.")}</p>}
            </section>
          )}
          {top && (
            <section className="ui-card perf-top">
              <h2>{t("Top 10 · {{metric}}", { metric: t(METRIC_LABELS[sort] || "Views") })}</h2>
              <Chart type="bar" height={Math.max(260, 34 * top.count)} options={top.options} series={top.series} />
            </section>
          )}
        </div>
      )}

      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <SearchInput value={search} onChange={reset(setSearch)} placeholder={t("Search video . . .")} />
          <select className="ui-input perf-sort" aria-label={t("Rank by")} value={sort} onChange={(e) => reset(setSort)(e.target.value)}>
            {[...columns, { sort: "trend", label: "Biggest rise" }, { sort: "totalViews", label: "Total views" }, { sort: "recent", label: "Most recent" }].map((c) => (
              <option key={c.sort} value={c.sort}>
                {t("Rank by")} : {t(c.label)}
              </option>
            ))}
          </select>
        </div>

        {error ? (
          <EmptyState icon="ri-error-warning-line">
            {t("The list could not be loaded.")}{" "}
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => load(true)}>
              {t("Retry")}
            </button>
          </EmptyState>
        ) : !data ? (
          <TableSkeleton />
        ) : !data.items.length ? (
          <EmptyState icon="ri-film-line">{t("No video")}</EmptyState>
        ) : (
          <div className="ui-table-wrap perf-table-wrap">
            <table className="ui-table perf-table">
              <thead>
                <tr>
                  <th className="perf-sticky perf-sticky-rank">#</th>
                  <th className="perf-sticky perf-sticky-title">{t("Video")}</th>
                  {columns.map((c) => (
                    <th key={c.sort} title={c.title ? t(c.title) : undefined}>
                      <button type="button" className="perf-sort-head" aria-pressed={sort === c.sort} onClick={() => reset(setSort)(c.sort)}>
                        {t(c.label)}
                        {sort === c.sort && <i className="ri-arrow-down-s-line" aria-hidden="true"></i>}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="ledger-row" onClick={() => setOpened(item.id)}>
                    <td className="ui-cell-muted perf-sticky perf-sticky-rank">{item.rank}</td>
                    <td className="perf-sticky perf-sticky-title">
                      <div className="ui-media">
                        {item.thumbnail ? (
                          <img className="ui-media-thumb" src={item.thumbnail} alt="" loading="lazy" />
                        ) : (
                          <span className="ui-media-thumb perf-thumb-empty" aria-hidden="true">
                            <i className={item.isShort ? "ri-smartphone-line" : "ri-film-line"}></i>
                          </span>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <Link to={`/dashboard/video-info/${item.id}`} className="ui-media-title" title={item.title} onClick={(e) => e.stopPropagation()}>
                            {item.title}
                          </Link>
                          <span className="ui-media-meta">
                            {item.isShort && format === "all" && <Badge color="blue">{t("Short")}</Badge>}
                            {item.isTrending && <Badge color="amber">{t("Trending")}</Badge>}
                            {item.contentType === "exclusive" && <Badge color="violet">{t("Exclusive content")}</Badge>}
                            {!item.online && <Badge color="slate">{t("Not online")}</Badge>}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="perf-views">
                        <strong>{fmt(item.views)}</strong>
                        <Trend item={item} />
                      </span>
                      <span className="ui-media-meta">{t("{{count}} in total", { count: item.totalViews, formatted: fmt(item.totalViews) })}</span>
                    </td>
                    <td>{item.watchSeconds ? formatDuration(item.watchSeconds) : "—"}</td>
                    <td>
                      {item.started >= 5 ? percent(item.completion) : "—"}
                      <span className="ui-media-meta">
                        {t("{{completed}} / {{started}} plays", { completed: fmt(item.completed), started: fmt(item.started) })}
                        {item.jwCompleteRate !== null ? ` · JW ${item.jwCompleteRate} %` : ""}
                      </span>
                    </td>
                    <td>
                      {item.started >= 5 ? (
                        <span className="perf-meter" title={percent(item.watched)}>
                          <span className="perf-meter-bar">
                            <span style={{ width: `${Math.min(100, item.watched || 0)}%` }}></span>
                          </span>
                          {percent(item.watched)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{fmt(item.comments)}</td>
                    <td>{fmt(item.likes)}</td>
                    {format !== "videos" && <td>{item.isShort ? fmt(item.shares) : "—"}</td>}
                    <td>
                      {fmt(item.thanks)}
                      {item.thanksUsd > 0 && <span className="ui-media-meta">{formatMoney(item.thanksUsd, "USD")}</span>}
                    </td>
                    <td>{fmt(item.watchlist)}</td>
                    <td>
                      {item.sales ? fmt(item.sales) : "—"}
                      {item.revenue.length > 0 && (
                        <span className="ui-media-meta">{item.revenue.map((r) => formatMoney(r.total, r.currency)).join(" + ")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pagination page={data.page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />}
        <p className="ui-hint perf-hint">
          {t("Views: the counter of the platform (like the dashboard). Plays: started in the app or on the website; watched to the end means 90 % at least, shown from 5 plays. JW: completion rate measured by JW Player over the whole life of the video.")}{" "}
          {t("Watch time is estimated from where each viewer stopped. The arrow next to the views compares with the previous period of the same length.")}
          {data && !data.viewsComplete && data.viewsTrackedSince ? ` ${t("Views are counted since {{date}}, when the daily history started.", { date: new Date(data.viewsTrackedSince).toLocaleDateString() })}` : ""}
        </p>
      </section>
      {opened && (
        <VideoPerformanceDetail
          id={opened}
          period={periodParams}
          averages={totals}
          onClose={() => setOpened(null)}
          onPrevious={at > 0 ? () => setOpened(order[at - 1]) : null}
          onNext={at >= 0 && at < order.length - 1 ? () => setOpened(order[at + 1]) : null}
        />
      )}
    </Page>
  );
};

export default VideoPerformance;
